// LandingAurora — landing-page-only variant of the §6 aurora (DESIGN_SYSTEM.md).
// The canonical app-shell Aurora (src/design-system/primitives/Aurora.tsx) stays
// the reference for in-app screens; this variant exists because a marketing page
// is a tall scrolling document, not a fixed shell frame:
//
// - Blobs are distributed down the FULL page height (absolute layer inside the
//   page's relative root), not pinned to one viewport, so the field stays alive
//   through the whole scroll.
// - Each blob gets its own parallax factor: an rAF-throttled scroll handler
//   writes translate3d directly to a per-blob WRAPPER element (no React
//   re-render per scroll frame). The float drift animation lives on the INNER
//   element, so parallax transform and float transform never fight over one
//   element's `transform`.
// - Color/blur/duration cycle through the existing --ds-aurora-blob-1..4 tokens
//   verbatim, so both themes and any future token tuning apply here for free.
// - The canvas wash stays on a separate viewport-fixed layer — stretching the
//   radial wash over the full document height would distort it.
// - prefers-reduced-motion: parallax is skipped entirely (blobs sit static at
//   their page positions); float drift is also disabled at mount.
import { useEffect, useRef, type CSSProperties } from 'react';

interface LandingBlobSpec {
  top: string;      // % of full page height
  left?: string;
  right?: string;
  size: string;
  tokenSet: 1 | 2 | 3 | 4; // --ds-aurora-blob-N color/blur/dur
  float: 'ds-float-y' | 'ds-float-y2';
  delay: string;
  /** Scroll offset multiplier. 0 = scrolls with the page; negative = lags
      behind (reads as farther away); positive = leads (reads as nearer). */
  parallax: number;
}

// Hand-placed spread: left-edge-weighted near the top (echoing the shell aurora's
// sidebar trio), alternating edges further down so every viewport of the scroll
// has ambient color without any two neighbors sharing an edge, size, or speed.
const BLOBS: LandingBlobSpec[] = [
  { top: '-4%',  left: '-9vw',   size: '48vmin', tokenSet: 1, float: 'ds-float-y',  delay: '0s',   parallax: -0.12 },
  { top: '2%',   right: '6vw',   size: '44vmin', tokenSet: 4, float: 'ds-float-y2', delay: '-3s',  parallax: -0.22 },
  { top: '9%',   left: '-3vw',   size: '40vmin', tokenSet: 2, float: 'ds-float-y2', delay: '-4s',  parallax: -0.05 },
  { top: '16%',  right: '-6vw',  size: '36vmin', tokenSet: 3, float: 'ds-float-y',  delay: '-8s',  parallax: -0.3 },
  { top: '24%',  left: '4vw',    size: '34vmin', tokenSet: 1, float: 'ds-float-y2', delay: '-6s',  parallax: -0.18 },
  { top: '31%',  right: '2vw',   size: '42vmin', tokenSet: 2, float: 'ds-float-y',  delay: '-2s',  parallax: -0.08 },
  { top: '39%',  left: '-7vw',   size: '46vmin', tokenSet: 3, float: 'ds-float-y2', delay: '-10s', parallax: -0.26 },
  { top: '47%',  right: '-4vw',  size: '38vmin', tokenSet: 4, float: 'ds-float-y',  delay: '-5s',  parallax: -0.14 },
  { top: '55%',  left: '2vw',    size: '32vmin', tokenSet: 2, float: 'ds-float-y',  delay: '-7s',  parallax: -0.32 },
  { top: '62%',  right: '5vw',   size: '44vmin', tokenSet: 1, float: 'ds-float-y2', delay: '-1s',  parallax: -0.1 },
  { top: '70%',  left: '-5vw',   size: '40vmin', tokenSet: 4, float: 'ds-float-y',  delay: '-9s',  parallax: -0.2 },
  { top: '78%',  right: '-7vw',  size: '48vmin', tokenSet: 3, float: 'ds-float-y2', delay: '-3.5s', parallax: -0.06 },
  { top: '86%',  left: '3vw',    size: '36vmin', tokenSet: 2, float: 'ds-float-y2', delay: '-11s', parallax: -0.24 },
  { top: '94%',  right: '3vw',   size: '42vmin', tokenSet: 1, float: 'ds-float-y',  delay: '-6.5s', parallax: -0.16 },
];

function blobInnerStyle(b: LandingBlobSpec, reducedMotion: boolean): CSSProperties {
  return {
    width: b.size,
    height: b.size,
    borderRadius: '50%',
    background: `radial-gradient(circle, var(--ds-aurora-blob-${b.tokenSet}), transparent 70%)`,
    filter: `blur(var(--ds-aurora-blob-${b.tokenSet}-blur))`,
    animation: reducedMotion
      ? 'none'
      : `${b.float} var(--ds-aurora-blob-${b.tokenSet}-dur) ease-in-out infinite`,
    animationDelay: b.delay,
    willChange: 'transform',
  };
}

export function LandingAurora() {
  const wrappersRef = useRef<(HTMLDivElement | null)[]>([]);
  const reducedMotion =
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  useEffect(() => {
    if (reducedMotion) return;
    let raf = 0;
    const apply = () => {
      raf = 0;
      const y = window.scrollY;
      BLOBS.forEach((b, i) => {
        const el = wrappersRef.current[i];
        if (el) el.style.transform = `translate3d(0, ${(y * b.parallax).toFixed(1)}px, 0)`;
      });
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(apply);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    apply();
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [reducedMotion]);

  return (
    <>
      {/* Canvas wash — viewport-fixed so the radial gradient keeps its intended
          geometry instead of stretching over the full document height. */}
      <div
        aria-hidden
        style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', background: 'var(--ds-canvas-wash)' }}
      />
      {/* Blob field — spans the full page height inside the page's relative root. */}
      <div
        aria-hidden
        style={{ position: 'absolute', inset: 0, overflow: 'hidden', zIndex: 0, pointerEvents: 'none' }}
      >
        {BLOBS.map((b, i) => (
          <div
            key={i}
            ref={(el) => (wrappersRef.current[i] = el)}
            style={{
              position: 'absolute',
              top: b.top,
              left: b.left,
              right: b.right,
              willChange: 'transform',
            }}
          >
            <div style={blobInnerStyle(b, reducedMotion)} />
          </div>
        ))}
      </div>
    </>
  );
}
